import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ConfigurationService } from './configuration.service';
import { Observable, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private ACCESS_TOKEN = 'access_token';
  private REFRESH_TOKEN = 'refresh_token';
  private EXPIRES_AT = 'expires_at';

  private serviceToken: string | null = null;
  private tokenExpiry: number | null = null;

  baseUrl = `${environment.baseUrls.keycloak.url}/realms/${environment.baseUrls.keycloak.realm}/protocol/openid-connect/token`;
  private astragate_token_url = `${environment.baseUrls.astragate_token}`;

  private readonly MFA_CLIENT_ID = `${environment.baseUrls.keycloak.clientId}`;;
  private readonly MFA_BASE = `${environment.baseUrls.keycloak.url}/realms/${environment.baseUrls.keycloak.realm}/mfa`;
  private readonly TOKEN_URL = `${environment.baseUrls.keycloak.url}/realms/${environment.baseUrls.keycloak.realm}/protocol/openid-connect/token`;
  private readonly LOGOUT_URL = `${environment.baseUrls.keycloak.url}/realms/${environment.baseUrls.keycloak.realm}/protocol/openid-connect/logout`;

  constructor(
    private http: HttpClient,
    private configService: ConfigurationService,
  ) {}

  mfaLoginStart(username: string, password: string): Observable<any> {
    const body = {
      clientId: this.MFA_CLIENT_ID,
      username,
      password,
    };

    return this.http.post(`${this.MFA_BASE}/login/start`, body, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    });
  }

  mfaSendCode(loginTicket: string, factorId: string): Observable<any> {
    const body = {
      clientId: this.MFA_CLIENT_ID,
      loginTicket,
      factorId,
    };

    return this.http.post(`${this.MFA_BASE}/login/code`, body, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    });
  }

  mfaGetTokens(loginTicket: string, fields: any = {}): Observable<any> {
    const body = new URLSearchParams({
      grant_type: 'password',
      client_id: this.MFA_CLIENT_ID,
      login_ticket: loginTicket,
      scope: 'openid profile email organization',
      ...fields,
    });

    return this.http.post(this.TOKEN_URL, body.toString(), {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    });
  }

  getMfaFactors(): Observable<any[]> {
    return this.http.get<any[]>(`${this.MFA_BASE}/factors`, {
      headers: this.authHeaders(),
    });
  }

  startVerification(factorId: string): Observable<any> {
    return this.http.post(
      `${this.MFA_BASE}/verifications`,
      { factorId },
      { headers: this.authHeaders() },
    );
  }

  confirmVerification(verificationId: string, code: string): Observable<any> {
    return this.http.post(
      `${this.MFA_BASE}/verifications/confirm`,
      { verificationId, code },
      { headers: this.authHeaders() },
    );
  }

  resendVerification(verificationId: string): Observable<any> {
    return this.http.post(
      `${this.MFA_BASE}/verifications/resend`,
      { verificationId },
      { headers: this.authHeaders() },
    );
  }

  setupSms(phone: string, verificationId?: string): Observable<any> {
    const body: any = { phone };
    if (verificationId) body.verificationId = verificationId;
    return this.http.post(`${this.MFA_BASE}/sms/setup`, body, {
      headers: this.authHeaders(),
    });
  }

  setupEmail(verificationId?: string): Observable<any> {
    const body: any = {};
    if (verificationId) body.verificationId = verificationId;
    return this.http.post(`${this.MFA_BASE}/email/setup`, body, {
      headers: this.authHeaders(),
    });
  }

  setupTotp(verificationId?: string): Observable<any> {
    const body: any = {};
    if (verificationId) body.verificationId = verificationId;
    return this.http.post(`${this.MFA_BASE}/totp/setup`, body, {
      headers: this.authHeaders(),
    });
  }

  confirmSetup(setupId: string, code: string, label?: string): Observable<any> {
    const body: any = { setupId, code };
    if (label) body.label = label;
    return this.http.post(`${this.MFA_BASE}/setup/confirm`, body, {
      headers: this.authHeaders(),
    });
  }

  resendSetup(setupId: string): Observable<any> {
    return this.http.post(
      `${this.MFA_BASE}/setup/resend`,
      { setupId },
      { headers: this.authHeaders() },
    );
  }

  removeFactor(factorId: string, verificationId: string): Observable<any> {
    return this.http.delete(`${this.MFA_BASE}/factors/${factorId}`, {
      headers: this.authHeaders(),
      body: { verificationId },
    });
  }

  private authHeaders(): HttpHeaders {
    const token = this.getAccessToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  login(payload: any) {
    const { username, password } = payload;

    const body = new URLSearchParams();
    body.set('client_id', `${environment.baseUrls.keycloak.clientId}`);
    body.set('grant_type', 'password');
    body.set('scope', 'openid profile email organization');
    body.set('username', username);
    body.set('password', password);

    return this.http.post(`${this.baseUrl}`, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  refreshToken() {
    const refreshToken = this.getRefreshToken();

    const body = new URLSearchParams();
    body.set('client_id', this.MFA_CLIENT_ID);
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken || '');

    return this.http.post(this.TOKEN_URL, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  resetPassword(payload: {
    currentPassword: string;
    newPassword: string;
    repeatPassword: string;
  }) {
    return this.http.put(`${this.astragate_token_url}/reset-password`, payload);
  }

  getServiceAccountToken(): Observable<string> {
    return this.configService.getAPICredentials().pipe(
      switchMap((res) => {
        const creds = res?.data;
        const clientId = creds?.clientId;
        const clientSecret = creds?.clientSecrete;

        return this.configService.getGenerateToken(clientId, clientSecret);
      }),
      tap((res) => {
        const data = res?.data;
        this.serviceToken = data?.accessToken;
      }),
      switchMap(() => of(this.serviceToken!)),
    );
  }

  setSession(authResult: any) {
    const expiresAt = Date.now() + authResult.expires_in * 1000;

    sessionStorage.setItem(this.ACCESS_TOKEN, authResult.access_token);
    sessionStorage.setItem(this.REFRESH_TOKEN, authResult.refresh_token);
    sessionStorage.setItem(this.EXPIRES_AT, expiresAt.toString());
  }

  getAccessToken() {
    return sessionStorage.getItem(this.ACCESS_TOKEN);
  }

  getUserData() {
    const token = this.getAccessToken();
    const url = `${environment.baseUrls.keycloak.url}/realms/${environment.baseUrls.keycloak.realm}/protocol/openid-connect/userinfo`;

    return this.http.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  register(body: any) {
    const url = `${environment.baseUrls.astragate_token}/onboard`;
    return this.http.post(`${url}`, body);
  }

  getRefreshToken() {
    return sessionStorage.getItem(this.REFRESH_TOKEN);
  }

  isAuthenticated(): boolean {
    const expiresAt = sessionStorage.getItem(this.EXPIRES_AT);
    return !!expiresAt && Date.now() < +expiresAt;
  }

  clearSession() {
    return sessionStorage.clear();
  }

  logout() {
    const refreshToken = this.getRefreshToken();

    const body = new URLSearchParams();
    body.set('client_id', this.MFA_CLIENT_ID);
    body.set('refresh_token', refreshToken || '');

    return this.http.post(this.LOGOUT_URL, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }
}
